import { createApp, h } from 'vue';

import CustomerStories from './components/CustomerStories.vue';
import Title from './components/Title.vue';

/* Global store */
import store from './store.js';
import installTitleSync from './helpers/title.js';

/* App */
const app = document.querySelector('#customer-stories');
const title = document.querySelector('#customer-stories-title');

/*
 * Inline replacement for vue-click-outside-element using Vue 3 directive hooks.
 * The handler is tracked in a module-level WeakMap so we don't pollute the DOM
 * with custom properties, attached to document in mounted() (after the element
 * is in the DOM), and refreshed in updated() so binding.value changes (e.g.
 * the bound method getting recreated after a parent rerender) are picked up.
 */
const clickOutsideHandlers = new WeakMap();

const buildHandler = (element, binding) => event => {
	if (element !== event.target && !element.contains(event.target)) {
		binding.value?.(event);
	}
};

const clickOutsideElement = {
	mounted(element, binding) {
		const handler = buildHandler(element, binding);
		clickOutsideHandlers.set(element, handler);
		document.addEventListener('click', handler);
	},
	updated(element, binding) {
		if (binding.value === binding.oldValue) {
			return;
		}

		const previous = clickOutsideHandlers.get(element);
		if (previous) {
			document.removeEventListener('click', previous);
		}

		const handler = buildHandler(element, binding);
		clickOutsideHandlers.set(element, handler);
		document.addEventListener('click', handler);
	},
	unmounted(element) {
		const handler = clickOutsideHandlers.get(element);
		if (handler) {
			document.removeEventListener('click', handler);
			clickOutsideHandlers.delete(element);
		}
	},
};

/* Mount a Vue 3 app with the shared store and the click-outside directive */
function bootstrap(component, props, mountSelector) {
	const instance = createApp({ render: () => h(component, props) });
	instance.config.globalProperties.$store = store;
	instance.directive('click-outside-element', clickOutsideElement);
	instance.mount(mountSelector);
}

/*
 * Wire up the page-title/URL side effects from the topic filter selection.
 * The initial hero title is whatever the njk rendered into the h1; the
 * cleared-state title is supplied by the page via data-cleared-title (only
 * needed on topic.njk, since on index.njk the initial h1 already is it).
 */
installTitleSync(store, {
	initialTitle: title.textContent.trim(),
	clearedTitle: app.dataset.clearedTitle || title.textContent.trim(),
});

/* Hero title */
bootstrap(Title, {}, '#customer-stories-title');

/* Story browser */
bootstrap(CustomerStories, { filterTopic: app.dataset.filterTopic }, '#customer-stories');

/* Clear the FOUC-prevention margin now that Vue has mounted into the element (Vue 3 mounts inside the target rather than replacing it) */
app.style.marginTop = 0;
